import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Facility from "../../shared/types/Facility";
import CustCodeField from "@/components/CustCodeField";
import { InputFieldBox } from "@/components/InputField";
import { DateFieldBox } from "@/components/DateField";
import InfoDisplay from "@/components/InfoDisplay";
import SidebarNavigation from "@/components/SidebarNavigation";
import Layout from "../../shared/components/Layout";
import AlertMessages from "@/components/AlertMessages";
import getRegistration from "../api/getRegistration";
import { RegistrationParams } from "../types";
import confirmRegistration from "../api/confirmRegistration";
import clearLocalStorage from "@/utils/clearLocalStorage";
import { getFormattedToday } from "@/utils/format";

const Registration: React.FC = () => {
  const addMonths = (dateString: string, add: number): string => {
    const parts = dateString.split('-');
    let year = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);

    month += add;

    if (month > 12) {
        year += 1;
        month -= 12;
    }

    return `${year}/${month.toString().padStart(2, '0')}`;
  }

  const navigate = useNavigate();
  const location = useLocation();

  const [alert, setAlert] = useState({
    open: false,
    messages: [],
  })

  const [facility, setFacility] = useState<Facility>();
  const [lensPrices, setLensPrices] = useState({
    both: {
      id: "",
      price: "",
    },
    oneSide: {
      id: "",
      price: "",
    }
  });
  const [customer, setCustomer] = useState({
    custCodeSuffix: "",
  })
  const [applicant, setApplicant] = useState({
    nameKana: "",
    birthday: "",
  })
  const [isSameApplicant, setIsSameApplicant] = useState<boolean>(true);
  const [consumer, setConsumer] = useState({
    nameKana: "",
  })
  const [flatRateAgreement, setFlatRateAgreement] = useState({
    lensPriceId: "",
    price: "",
    plan: "",
    startDate: getFormattedToday(true),
    startMonth: getFormattedToday(false),
  })

  const [usageFeeOccurMonth, setUsageFeeOccurMonth] = useState<string>(addMonths(getFormattedToday(false), 2));
  const [usageFeeFirstPaymentMonth, setUsageFeeFirstPaymentMonth] = useState<string>(addMonths(getFormattedToday(false), 3));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRegistration();
        
        setFacility(response.data.facility);
        setLensPrices(response.data.lensPrices);
      } catch (error) {
        console.error("Error fetching flat rate lens data:", error);
      }
    };

    fetchData();

    const fromConfirmPage = location.state?.fromConfirmPage;
    if(fromConfirmPage) {
      const storedCustomer = localStorage.getItem("customer");
      if(storedCustomer) {
        setCustomer(JSON.parse(storedCustomer));
      }

      const storedApplicant = localStorage.getItem("applicant");
      if(storedApplicant) {
        setApplicant(JSON.parse(storedApplicant));
      }

      const storedConsumer = localStorage.getItem("consumer");
      if(storedConsumer) {
        setConsumer(JSON.parse(storedConsumer));
      }

      const storedFlatRateAgreement = localStorage.getItem("flatRateAgreement");
      if(storedFlatRateAgreement) {
        setFlatRateAgreement(JSON.parse(storedFlatRateAgreement));
      }

      const storedIsSameApplicant = localStorage.getItem("isSameApplicant");
      if(storedIsSameApplicant) {
        setIsSameApplicant(JSON.parse(storedIsSameApplicant));
      }

      const storedFacilityCode = localStorage.getItem("facilityCode");
      if(storedFacilityCode && facility) {
        const facilityCode = JSON.parse(storedFacilityCode);
        setFacility({ ...facility, facilityCode });
      }

      const storedFlatRateCode = localStorage.getItem("flatRateCode");
      if(storedFlatRateCode && facility) {
        const flatRateCode = JSON.parse(storedFlatRateCode);
        setFacility({ ...facility, flatRateCode });
      }

      const storedUsageFeeOccurMonth = localStorage.getItem("usageFeeOccurMonth");
      if(storedUsageFeeOccurMonth) {
        setUsageFeeOccurMonth(JSON.parse(storedUsageFeeOccurMonth));
      }

      const storedUsageFeeFirstPaymentMonth = localStorage.getItem("usageFeeFirstPaymentMonth");
      if(storedUsageFeeFirstPaymentMonth) {
        setUsageFeeFirstPaymentMonth(JSON.parse(storedUsageFeeFirstPaymentMonth));
      }
    } else {
      clearLocalStorage();
    }
  }, [location.state?.fromConfirmPage]);

  useEffect(() => {
    setFlatRateAgreement({...flatRateAgreement, plan: "both", price: lensPrices?.both?.price, lensPriceId: lensPrices?.both?.id })
  }, [lensPrices]);

  useEffect(() => {
    if(isSameApplicant) setConsumer({nameKana: applicant.nameKana});
  }, [isSameApplicant, applicant.nameKana])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const params: RegistrationParams = {
      facility: { facilityCode: facility?.facilityCode},
      customer: customer,
      applicant: applicant,
      consumer: consumer,
      flatRateAgreement: flatRateAgreement,
    }

    try {
      const res = await confirmRegistration(params);
      setAlert({open: false, messages: []});
      localStorage.setItem("customer", JSON.stringify(customer));
      localStorage.setItem("applicant", JSON.stringify(applicant));
      localStorage.setItem("consumer", JSON.stringify(consumer));
      localStorage.setItem("flatRateAgreement", JSON.stringify(flatRateAgreement));
      localStorage.setItem("isSameApplicant", JSON.stringify(isSameApplicant));
      localStorage.setItem("facilityCode", JSON.stringify(facility?.facilityCode));
      localStorage.setItem("flatRateCode", JSON.stringify(facility?.flatRateCode));
      localStorage.setItem("usageFeeOccurMonth", JSON.stringify(usageFeeOccurMonth));
      localStorage.setItem("usageFeeFirstPaymentMonth", JSON.stringify(usageFeeFirstPaymentMonth));
      if (res.data.orderId){
        localStorage.setItem("orderId", JSON.stringify(res.data.orderId));
      }

      navigate("/facility/flat_rates/registration/confirm");
      window.scrollTo(0, 0);
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.errors) {
        setAlert({open: true, messages: err.response.data.errors.fullMessages});
        window.scrollTo(0, 0);
      } else {
        setAlert({open: false, messages: []});
        console.log('An unexpected error occurred:', err);
      }
    }
  }

  return (
    <React.Fragment>
      <Layout>
        <main className="wrapper" id="page-facility_login">
          <section className="sec-contact">
            <div className="inner">
              <h1 className="c-ttl__01">新規契約申込入力</h1>
              <AlertMessages open={alert.open} messages={alert.messages} />
              <div className="contact-box">
                <div className="contact-box__left">
                  <div className="mbpc-98 mbsp-32">
                    <form onSubmit={handleSubmit}>
                      <div className="contact-box__form js-item" id="id-01">
                        <h2 className="c-ttl__02">お客様情報</h2>
                        <CustCodeField
                          label="お客様ID"
                          placeholder={"1234"}
                          value={customer.custCodeSuffix}
                          onChange={event => setCustomer({...customer, custCodeSuffix: event.target.value })}
                          facilityCode={facility?.flatRateCode}
                          required={true}
                          itemStart={true}
                          note={"※4桁の数字でご入力"}
                        />
                        <InputFieldBox
                          label={"申込者 (カナ)"}
                          placeholder={"レンズ　ハナコ"}
                          value={applicant.nameKana}
                          onChange={event => setApplicant({...applicant, nameKana: event.target.value })}
                          required={true}
                        />
                        <DateFieldBox
                          label={<>申込者生年月日<br/>(仮パスワード)</>}
                          pattern={"d{4}-d{2}-d{2}"}
                          value={applicant.birthday}
                          onChange={event => setApplicant({...applicant, birthday: event.target.value })}
                          required={true}
                        />
                        <div className="contact-box__item contact-box__item--start">
                          <label className="contact-box__ttl">
                            <span>使用者（カナ）</span>
                            <small>必須</small>
                          </label>
                          <div className="contact-box__ct">
                            <ul className="list-radio">
                              <li className="list-radio__item">
                                <label className="checkbox-item correct-box__form-check">
                                  <input type="radio" checked={isSameApplicant} id="checkShowIput02" onChange={() => setIsSameApplicant(true)} />
                                  <span className="checkmark"></span> 申込者と同一 </label>
                              </li>
                              <li className="list-radio__item">
                                <label className="checkbox-item correct-box__form-check">
                                  <input type="radio" checked={isSameApplicant === false} id="checkShowIput" onChange={() => setIsSameApplicant(false)} />
                                  <span className="checkmark"></span> 申込者と異なる </label>
                              </li>
                            </ul>
                            { isSameApplicant === false &&
                              <div id="show-input" className="mtpc-10 mtsp-3 on">
                                <input
                                  type="text"
                                  className="input"
                                  placeholder="レンズ　ハナコ"
                                  value={consumer.nameKana}
                                  onChange={event => setConsumer({...consumer, nameKana: event.target.value})}
                                />
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                      <div className="contact-box__form mbsp-29 js-item" id="id-02">
                        <h2 className="c-ttl__02">契約詳細</h2>
                        <div className="contact-box__item contact-box__item--start">
                          <label className="contact-box__ttl">
                            <span>ご契約プラン</span>
                            <small>必須</small>
                          </label>
                          <div className="contact-box__ct">
                            <ul className="list-radio">
                              <li className="list-radio__item">
                                <label className="checkbox-item correct-box__form-check">
                                    <input type="radio" checked={flatRateAgreement.plan === "both"} onChange={() => {
                                      setFlatRateAgreement({...flatRateAgreement, plan: "both", lensPriceId: lensPrices?.both?.id, price: lensPrices?.both?.price })
                                    }} />
                                    <span className="checkmark"></span>両眼プラン ({lensPrices?.both?.price}円/月)
                                </label>
                              </li>
                              <li className="list-radio__item">
                                <label className="checkbox-item correct-box__form-check">
                                    <input type="radio" checked={flatRateAgreement.plan === "right"} onChange={() => {
                                      setFlatRateAgreement({...flatRateAgreement, plan: "right", lensPriceId: lensPrices?.oneSide?.id, price: lensPrices?.oneSide?.price })
                                    }} />
                                  <span className="checkmark"></span>片眼プラン(右眼)({lensPrices?.oneSide?.price}円/月)
                                </label>
                              </li>
                              <li className="list-radio__item">
                                <label className="checkbox-item correct-box__form-check">
                                  <input type="radio" checked={flatRateAgreement.plan === "left"} onChange={() => {
                                    setFlatRateAgreement({...flatRateAgreement, plan: "left", lensPriceId: lensPrices?.oneSide?.id, price: lensPrices?.oneSide?.price })
                                  }} />
                                  <span className="checkmark"></span>片眼プラン(左眼)({lensPrices?.oneSide?.price}円/月)
                                </label>
                              </li>
                            </ul>
                          </div>
                        </div>
                        <DateFieldBox
                          label={"サービス利用開始日"}
                          pattern={"d{4}-d{2}-d{2}"}
                          value={flatRateAgreement.startDate}
                          required={true}
                          onChange={event => {
                            setFlatRateAgreement({...flatRateAgreement, startDate: event.target.value })
                          }}
                        />
                        <DateFieldBox
                          type="month"
                          label={"サービス利用開始月"}
                          value={flatRateAgreement.startMonth}
                          required={true}
                          onChange={event => {
                            setFlatRateAgreement({...flatRateAgreement, startMonth: event.target.value });
                            setUsageFeeOccurMonth(addMonths(event.target.value, 2));
                            setUsageFeeFirstPaymentMonth(addMonths(event.target.value, 3));
                          }}
                        />
                        <InfoDisplay label="利用料請求発生月" value={usageFeeOccurMonth} />
                        <InfoDisplay label="利用料初回決済月（月末）" value={usageFeeFirstPaymentMonth} />
                      </div>
                      <button type="submit" className="c-btn__01">入力内容の確認</button>
                    </form>
                  </div>
                </div>
                <SidebarNavigation items={[{ id: "id-01", label: "お客様情報" }, { id: "id-02", label: "契約詳細" }]} />
              </div>
            </div>
          </section>
        </main>
      </Layout>
    </React.Fragment>
  )
}

export default Registration;